import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const GAP = 4;      // between the button and the menu
/** What the keyboard can land on: the menu's options, and the button it opens from. */
const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
const MARGIN = 8;   // kept clear at the edge of the window

interface FloatingMenuProps {
  /** The element the menu opens from (the three-dots button, or the box around it). */
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Width and any extra classes, e.g. "w-40". */
  className?: string;
  /**
   * Written out where the menu is used (`role="menu"`), so the Table Actions source
   * check can see that what is inside is a menu's items and not a strip of icons.
   */
  role?: 'menu';
  children: ReactNode;
}

/**
 * A row menu that is drawn over the page rather than inside the table.
 *
 * A menu placed inside a table is cut off wherever the table's box ends: with one
 * or two rows the table is shorter than the menu, so most of the options are
 * hidden and the admin has to scroll inside the table to reach them. This one is
 * positioned against the window instead, lined up with the right edge of its
 * button. It opens upwards when there is no room below, and scrolls inside itself
 * only when the window is too short to show it whole.
 *
 * Being drawn at the end of the page, its options no longer come next after the
 * button for somebody using the keyboard. So when it is opened from its button,
 * the first option takes the focus, and Escape hands the focus back to the button.
 */
export default function FloatingMenu({ anchor, onClose, className = '', role = 'menu', children }: FloatingMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return;
    const place = () => {
      const menu = menuRef.current;
      if (!menu) return;
      const a = anchor.getBoundingClientRect();
      // The menu's full height, whatever max-height the last pass left on it.
      const height = menu.scrollHeight + (menu.offsetHeight - menu.clientHeight);
      const width = menu.offsetWidth;
      const below = window.innerHeight - a.bottom - GAP - MARGIN;
      const above = a.top - GAP - MARGIN;
      const openUp = height > below && above > below;
      const room = Math.max(openUp ? above : below, 0);
      setPos({
        top: openUp ? a.top - GAP - Math.min(height, room) : a.bottom + GAP,
        left: Math.max(MARGIN, Math.min(a.right - width, window.innerWidth - width - MARGIN)),
        maxHeight: room,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor]);

  // Opened from its button (the button, or something inside the box around it,
  // has the focus): the first option takes it. Opened any other way, the focus is
  // left where it is.
  //
  // Only once the menu has its place. Until then it is hidden, and a browser will
  // not move the focus into something hidden (jsdom will, which is how a version
  // that focused straight away passed its tests and did nothing in Chrome).
  // `placed` goes from false to true once per opening, so the re-placements on
  // scroll and resize do not pull the focus back.
  const placed = pos !== null;
  useEffect(() => {
    if (!placed || !anchor || !anchor.contains(document.activeElement)) return;
    menuRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true });
  }, [anchor, placed]);

  // Close on a click anywhere else, or Escape.
  useEffect(() => {
    if (!anchor) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchor.contains(target) || menuRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Back to the button the menu was opened from, if the focus was in the menu.
      const wasInMenu = menuRef.current?.contains(document.activeElement) ?? false;
      onClose();
      if (wasInMenu) (anchor.matches(FOCUSABLE) ? anchor : anchor.querySelector<HTMLElement>(FOCUSABLE))?.focus({ preventScroll: true });
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  if (!anchor) return null;

  return createPortal(
    <div
      ref={menuRef}
      role={role}
      // Measured first, shown once it has a place: it never flashes in a corner.
      style={pos
        ? { position: 'fixed', top: pos.top, left: pos.left, maxHeight: pos.maxHeight, zIndex: 140 }
        : { position: 'fixed', top: 0, left: 0, visibility: 'hidden', zIndex: 140 }}
      className={`overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body,
  );
}
